/**
 * @file 本文件为服务调用插件实现
 * @author PRD UX R&D Dept.
 * @example <caption>简单用法</caption>
 * import index from './index'
 * http.$index.get('/foo/login').then((response) => {
 *   if (response.status === 200) {
 *     console.log(`response.data=${response.data}`)
 *   }
 * })
 */

import Vue from 'vue';
import axios from 'axios';
import config from './axios.config.js';
import vue from '../main';
import router from '../router';
import store from '../store';
import requsetURL from '../../public/defaultRequest.js';
axios.defaults.timeout = config.timeout;
axios.defaults.headers = Object.assign(axios.defaults.headers, config.headers);
axios.defaults.baseURL = config.baseURL;
axios.defaults.retry = config.retry;
axios.defaults.retryDelay = config.retryDelay;
/**
 * 深度合并多个对象，返回合并后的新对象
 * @private
 * @param  {...Object} objs 多个对象
 * @return {Object}    返回合并后的新对象，原对象内容不变
 */
function merge(...objs) {
    let result = {};

    function assignValue(val, key) {
        if (typeof result[key] === 'object' && typeof val === 'object') {
            result[key] = merge(result[key], val);
        } else {
            result[key] = val;
        }
    }

    for (let obj of objs) {
        for (let key in obj) {
            assignValue(obj[key], key);
        }
    }
    return result;
}

/**
 * 判断给定参数是否`null`或空对象`{}`
 * @private
 * @param  {Object}  obj 待检测对象
 * @return {Boolean}  如果参数`obj`是`null`或空对象`{}`，那么返回`true`，否则返回`false`
 */
function isOwnEmpty(obj) {
    for (let name in obj) {
        if (obj.hasOwnProperty(name)) {
            return false; // 返回false，不为空对象
        }
    }
    return true; // 返回true，为空对象
}

const http = getInstances(config);

/**
 * 获得服务调用对象实例
 * @private
 * @param  {Object} conf `assets/axios.config.js`内定义的配置对象
 * @return {Object} 返回一个根据`assets/axios.config.js`配置文件生
 *                  成的服务调用对象集合
 */
function getInstances(conf) {
    let result = {};
    let domains = conf.root || null;
    let commonConfig = conf.commonConfig ? conf.commonConfig : {};

    // axios.defaults = merge(axios.defaults, commonConfig)
    let opts = merge(axios.defaults, commonConfig);
    for (let ky in opts) {
        axios.defaults[ky] = opts[ky];
    }
    // axios.defaults.headers['Content-Type'] = 'application/json';
    result.$http = axios;
    if (!isOwnEmpty(domains)) {
        for (let key in domains) {
            key = key.trim();
            if (key !== '' && key !== 'http') {
                // 忽略以"http"为属性名的节点
                let instance = axios.create(domains[key]);
                let domainName = `\$${key}`;
                // 为每一个实例添加上axios的静态方法
                instance.all = promises => {
                    return axios.all(promises);
                };
                instance.spread = cb => {
                    return axios.spread(cb);
                };
                instance.Cancel = () => {
                    return axios.Cancel;
                };
                instance.CancelToken = () => {
                    return axios.CancelToken;
                };
                instance.isCancel = () => {
                    return axios.isCancel;
                };
                result[domainName] = instance;
            }
        }
    }
    return result;
}

// 定义插件
const HttpPlugin = {
    install: Vue => {
        if (http === null) {
            Vue.prototype.$http = axios;
        } else {
            for (let domainName in http) {
                Vue.prototype[domainName] = http[domainName];
            }
        }
    }
};

/**
 * 请求拦截器
 * @param {Object} config `axios`配置对象
 * @returns {Object|Promise} 返回配置对象本身或者返回一个Promise对象，
 *                           参见{@link https://github.com/axios/axios Axios}官网
 */
function requestInterceptor(config) {
    // 将post方法的content-type 设置为 application/x-www-form-item-item-urlencoded
    // if(config.url.indexOf('downloadUserListInfo')>-1){
    //     config.headers.connection = 'keep-alive';
    // }
    config.headers.common.token = JSON.parse(sessionStorage.getItem('token'));
    config.headers.common.ip = sessionStorage.getItem('Ip');
    // config.headers.common.token = store.getters.getToken || sessionStorage.getItem('token');
    // config.headers.common.token = JSON.parse(store.getters.getToken) || sessionStorage.getItem('token');
    if (config.method === 'post') {
        // config.headers.common.token = store.getters.getToken;
        // config.headers.post['Content-Type'] = 'application/x-www-form-item-item-urlencoded'
        // let data = config.data
        // let param = new URLSearchParams()
        // for (let key in data) {
        //   param.append(key, data[key])
        // }
        // config.data = param
        let data = config.data;
        for (let key in data) {
            if (typeof data[key] === 'string') {
                // 特殊字符过滤
                // eslint-disable-next-line no-useless-escape
                // data[key] = data[key].replace(/[^\a-\z\A-\Z0-9\u4E00-\u9FA5\@\.]/g,'');
            }
            if (data[key] === null) data[key] = '';
        }
    }
    return merge(config);
}

/**
 * 请求错误处理
 * @param error
 * @returns {Promise} 返回一个Promise对象
 */
function requestError(error) {
    return Promise.reject(error);
}

/**
 * 响应拦截器
 * @param {Object} response `Axios`响应对象
 * @returns {Object|Promise} 返回`Axios`响应对象或Promise对象
 */
function responseInterceptor(response) {
    response = checkStatus(response);
    response = checkCode(response);
    return response;
}

const checkStatus = response => {
    // loading
    // 如果http状态码正常，则直接返回数据
    if (response && (response.status === 200 || response.status === 304 || response.status === 400)) {
        return response.data;
        // 如果不需要除了data之外的数据，可以直接 return response.data
    }
    if (response.status === 401) {
        // router.replace({
        //     path: '/',
        //     query: {redirect: router.currentRoute.fullPath}
        // });

        return false;
    }
    return {
        status: -404,
        msg: '网络异常'
    };
};

const checkCode = data => {
    if (data.status === -404) {
        showError(data.msg);
        return Promise.reject(data);
    }
    // 如果code异常(这里已经包括网络错误，服务器错误，后端抛出的错误)，可以弹出一个错误提示，告诉用户
    if (data.code) {
        // 业务接口错误
        if (data.code !== 200) {
            // 接口错误
            showError(data.msg);
            return Promise.reject(data);
        }
    }
    return data;
};

const showError = (errorMsg, alwaysDisplay) => {
    vue.$message.error(errorMsg || '服务调用出错');
};

function responseError(error) {
    if (error && error.response && error.response.status) {
    }
    if (error && error.response) {
        switch (error.response.status) {
        case 403:
            break;
        case 401:
            vue.$message.error('登陆失效，请重新登陆');
            window.sessionStorage.clear();
            setTimeout(function () {
                // window.location.replace(`http://10.1.102.237:8092/auth/logout?token=${window.sessionStorage.getItem('token')}&response_type=code&client_id=gongxiao_basic&redirect_uri=http://datago.fupin832.com/api/basic/auth/loginSuccess?state=kk`);
                // eslint-disable-next-line max-len
                window.location.replace(
                    `${requsetURL}/auth/logout?token=${JSON.parse(
                        window.sessionStorage.getItem('token')
                    )}&response_type=code&client_id=gongxiao_basic&redirect_uri=${requsetURL}/api/basic/auth/loginSuccess?state=kk`
                );
                // eslint-disable-next-line max-len
                // window.location.replace(`http://datago.fupin832.com/auth/logout?token=${window.sessionStorage.getItem('token')}&response_type=code&client_id=gongxiao_basic&redirect_uri=http://datago.fupin832.com/api/basic/auth/loginSuccess?state=kk`);
            }, 500);

            break;
        case 408:
            vue.$message.error('请求超时，请重试');
            break;
        default:
            vue.$message.error((error.response.data && error.response.data.message) || '请求错误，请重试');
            break;
        }
    }
    return Promise.reject(error);
}

// 默认服务调用拦截器设置
http.$http.interceptors.request.use(requestInterceptor, requestError);
http.$http.interceptors.response.use(responseInterceptor, responseError);

// 注册插件
Vue.use(HttpPlugin);

export default http;
